import { useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { defaultComponentCopy } from '../../../helpers/questionnaire_translations';

import React from 'react';
import QuestionWrapper from '../../core/ui/question_wrapper';
import QuestionHeading from '../../core/ui/question_heading';
import QuestionSubheading from '../../core/ui/question_subheading';
import TextInput from '../../core/ui/text_input';

import styles from './clinical_site_question.module.css';

const siteFromValue = (value) => {
  if (!value || !value.site_id) return null;

  return {
    site_id: value.site_id,
    site_name: value.site_name || '',
    location: value.location || '',
  };
};

const locationFor = (site) => site.location || [site.city, site.state_province].filter(Boolean).join(', ');

const nameCountsFor = (sites) =>
  sites.reduce((counts, site) => {
    const key = (site.site_name || '').toLowerCase();
    counts[key] = (counts[key] || 0) + 1;
    return counts;
  }, {});

/**
 * Clinical study step that captures the mentee's Subject ID and Site. Site is
 * chosen from a live type-ahead on the account's Site List (name or Site ID).
 * Site ID is never typed — it is always derived from the selected site.
 */
const ClinicalSiteQuestion = ({ question, current, total, value, onChange, onSubmit, onBack, onSearchSites, copy }) => {
  const text = { ...defaultComponentCopy('clinical_site'), ...copy };
  const initial = value && typeof value === 'object' ? value : {};
  const [subjectId, setSubjectId] = useState(initial.subject_id || '');
  const [selectedSite, setSelectedSite] = useState(() => siteFromValue(initial));
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [looking, setLooking] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const requestIdRef = useRef(0);
  const searchInputRef = useRef(null);

  useEffect(() => {
    onChange({
      subject_id: subjectId,
      site_id: selectedSite ? selectedSite.site_id : '',
      site_name: selectedSite ? selectedSite.site_name : '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectId, selectedSite]);

  useEffect(() => {
    if (!initial.site_id || (selectedSite && selectedSite.site_name) || typeof onSearchSites !== 'function') {
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    onSearchSites(initial.site_id)
      .then((data) => {
        if (requestId !== requestIdRef.current) return;
        const match = (data.sites || []).find((site) => site.site_id === initial.site_id);
        if (match) setSelectedSite(match);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runSearch = (term) => {
    const trimmed = term.trim();
    if (!trimmed || typeof onSearchSites !== 'function') {
      requestIdRef.current += 1;
      setResults([]);
      setLooking(false);
      setOpen(false);
      setHighlightIndex(-1);
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLooking(true);
    setError('');

    onSearchSites(trimmed)
      .then((data) => {
        if (requestId !== requestIdRef.current) return;
        const sites = data.sites || [];
        setResults(sites);
        setOpen(true);
        setHighlightIndex(sites.length === 1 ? 0 : -1);
      })
      .catch((searchError) => {
        if (requestId !== requestIdRef.current) return;
        setResults([]);
        setOpen(true);
        setError(searchError.message);
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLooking(false);
      });
  };

  const debouncedSearch = useDebouncedCallback(runSearch, 300);

  const applySite = (site) => {
    // Selecting a site should cancel any pending or in-flight searches
    debouncedSearch.cancel();
    requestIdRef.current += 1;
    setLooking(false);
    setSelectedSite(site);
    onChange({
      subject_id: subjectId,
      site_id: site.site_id,
      site_name: site.site_name,
    });
    setQuery('');
    setResults([]);
    setOpen(false);
    setHighlightIndex(-1);
    setError('');
  };

  const clearSite = () => {
    // Clearing should invalidate pending/in-flight searches to prevent stale repopulation
    debouncedSearch.cancel();
    requestIdRef.current += 1;
    setLooking(false);
    setSelectedSite(null);
    setQuery('');
    setResults([]);
    setOpen(false);
    setHighlightIndex(-1);
    setError('');
    setTimeout(() => {
      if (searchInputRef.current) searchInputRef.current.focus();
    }, 0);
  };

  const handleQueryChange = (next) => {
    // Invalidate any in-flight work as soon as the query changes
    debouncedSearch.cancel();
    requestIdRef.current += 1;
    setQuery(next);
    setError('');
    const trimmed = next.trim();
    // Clear old results immediately so users can't select stale entries while typing
    setResults([]);
    setHighlightIndex(-1);
    if (trimmed) {
      setOpen(true);
      setLooking(true);
      debouncedSearch(trimmed);
    } else {
      setOpen(false);
      setLooking(false);
      runSearch('');
    }
  };

  const handleSubmit = () => {
    if (!subjectId.trim() && !selectedSite) {
      setError(text.subject_and_site_required);
      return;
    }

    if (!subjectId.trim()) {
      setError(text.subject_required);
      return;
    }

    if (!selectedSite) {
      if (typeof onSearchSites !== 'function') {
        onSubmit();
        return;
      }

      setError(text.site_required);
      return;
    }

    onSubmit();
  };

  const handleSearchKeyDown = (event) => {
    if (!open || results.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightIndex((currentIndex) => (currentIndex + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightIndex((currentIndex) => (currentIndex <= 0 ? results.length - 1 : currentIndex - 1));
    } else if (event.key === 'Enter' && highlightIndex >= 0) {
      event.preventDefault();
      applySite(results[highlightIndex]);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      setHighlightIndex(-1);
    }
  };

  const nameCounts = nameCountsFor(results);
  const showDropdown = open && !selectedSite && (looking || results.length > 0 || query.trim().length > 0);

  return (
    <QuestionWrapper current={current} total={total} onSubmit={handleSubmit} onBack={onBack} isDisabled={looking}>
      <QuestionHeading>{question.heading || 'Your study information'}</QuestionHeading>
      {question.subHeading && <QuestionSubheading>{question.subHeading}</QuestionSubheading>}

      <div className={styles.field}>
        <label className={styles.label} htmlFor='clinical-site-subject-id'>
          {text.subject_id_label}
        </label>
        <TextInput
          id='clinical-site-subject-id'
          value={subjectId}
          onChange={setSubjectId}
          placeholder={text.subject_id_placeholder}
          ariaLabel={text.subject_id_label}
        />
      </div>

      <div className={styles.field}>
        <span className={styles.label} id='clinical-site-search-label'>
          {text.site_label}
        </span>

        {selectedSite ? (
          <div className={styles.confirmation}>
            <p className={styles.confirmationLine}>
              {text.site_name_label} <strong>{selectedSite.site_name || '—'}</strong>
            </p>
            <p className={styles.confirmationLine}>
              {text.site_id_label} <strong>{selectedSite.site_id}</strong>
            </p>
            <button type='button' className={styles.changeButton} onClick={clearSite}>
              {text.change_site}
            </button>
          </div>
        ) : (
          <div className={styles.search}>
            <TextInput
              inputRef={searchInputRef}
              value={query}
              onChange={handleQueryChange}
              onKeyDown={handleSearchKeyDown}
              onFocus={() => {
                if (results.length > 0 || query.trim()) setOpen(true);
              }}
              onBlur={() => {
                setTimeout(() => setOpen(false), 150);
              }}
              placeholder={text.search_placeholder}
              inputProps={{
                'aria-label': text.site_label,
                'aria-labelledby': 'clinical-site-search-label',
                role: 'combobox',
                'aria-expanded': showDropdown,
                'aria-controls': 'clinical-site-search-results',
                'aria-autocomplete': 'list',
                autoComplete: 'off',
              }}
            />
            {showDropdown && (
              <ul
                id='clinical-site-search-results'
                className={styles.results}
                role='listbox'
                aria-label={text.matching_sites}
              >
                {looking && results.length === 0 && <li className={styles.status}>{text.searching}</li>}
                {!looking && results.length === 0 && query.trim() && (
                  <li className={styles.status} role='status'>
                    {text.no_matches}
                  </li>
                )}
                {results.map((site, index) => {
                  const location = locationFor(site);
                  const duplicateName = nameCounts[(site.site_name || '').toLowerCase()] > 1;
                  const showLocation = Boolean(location) && (duplicateName || results.length > 1);

                  return (
                    <li key={site.site_id} role='presentation'>
                      <button
                        type='button'
                        role='option'
                        aria-selected={index === highlightIndex}
                        className={index === highlightIndex ? `${styles.result} ${styles.resultActive}` : styles.result}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => applySite(site)}
                      >
                        <span className={styles.resultName}>{site.site_name}</span>
                        {showLocation && <span className={styles.resultDetail}>{location}</span>}
                        <span className={styles.resultId}>{site.site_id}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>

      {error && (
        <p role='alert' className={styles.error}>
          {error}
        </p>
      )}
    </QuestionWrapper>
  );
};

export default ClinicalSiteQuestion;
